import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  CalendarRange,
  BookText,
  ShoppingCart,
  Boxes,
  Factory,
  LogOut,
  ShieldCheck,
  Eye,
  Menu,
  X,
  History,
  TrendingDown,
  Truck,
} from "lucide-react";

const nav = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/uretim-plani", label: "Üretim Planı / Siparişler", icon: CalendarRange, testid: "nav-plan" },
  { to: "/uretim-cizelgesi", label: "Üretim Çizelgesi", icon: Factory, testid: "nav-schedule" },
  { to: "/receteler", label: "Reçeteler", icon: BookText, testid: "nav-recipes" },
  { to: "/satin-alma", label: "Satın Alma", icon: ShoppingCart, testid: "nav-po" },
  { to: "/gelen-malzemeler", label: "Gelen Malzemeler", icon: Truck, testid: "nav-receipts" },
  { to: "/malzemeler", label: "Malzemeler", icon: Boxes, testid: "nav-materials" },
  { to: "/tuketimler", label: "Tüm Tüketimler", icon: TrendingDown, testid: "nav-consumption" },
  { to: "/detayli-stok", label: "Detaylı Malzeme Stok Analiz", icon: LayoutDashboard, testid: "nav-detailed-stock" },
  { to: "/gecmis", label: "Geçmiş Hareketler", icon: History, testid: "nav-activity" },
  { to: "/yonetici", label: "Yönetici Paneli", icon: ShieldCheck, testid: "nav-admin", adminOnly: true },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const roleLabel = isAdmin ? "Yönetici" : user?.role === "user" ? "Kullanıcı" : "İzleyici (Salt Okunur)";
  const roleColor = isAdmin ? "#60A5FA" : user?.role === "user" ? "#34D399" : "#94A3B8";
  const roleBg = isAdmin ? "rgba(37,99,235,0.15)" : user?.role === "user" ? "rgba(16,185,129,0.15)" : "rgba(148,163,184,0.15)";

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-[#F8FAFC]">
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        className={`w-64 shrink-0 border-r border-[#212D40] bg-[#0F1520] flex flex-col fixed h-screen z-40 transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b border-[#212D40]">
          <div className="w-9 h-9 rounded-lg bg-[#2563EB] flex items-center justify-center">
            <Factory className="w-5 h-5 text-white" />
          </div>
          <span className="font-head text-xl font-bold tracking-wide">ATLANTA YAY - STOK TAKİP</span>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {nav.filter((n) => !n.adminOnly || isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#2563EB] text-white"
                    : "text-[#94A3B8] hover:bg-[#131A26] hover:text-white"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[#212D40]">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-[#64748B] truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#131A26] hover:text-white transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Çıkış Yap
          </button>
        </div>
      </aside>

      <div className="lg:ml-64 flex flex-col min-h-screen">
        <header className="h-16 border-b border-[#212D40] bg-[#0B0F17] flex items-center justify-between lg:justify-end px-4 md:px-6 gap-3 sticky top-0 z-20">
          <button
            className="lg:hidden text-[#94A3B8]"
            onClick={() => setOpen(true)}
            data-testid="sidebar-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div
            data-testid="role-indicator-badge"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
            style={{ background: roleBg, color: roleColor, border: `1px solid ${roleColor}66` }}
          >
            {isAdmin ? <ShieldCheck className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {roleLabel}
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-[1500px] w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
